import { useLayoutEffect, useRef, useState } from 'react';

/** Keep the selection attached to characters, not to inserted separators.
 * Controlled masks otherwise move it to the end and append the rest of a
 * replacement there. Capture before changing the canonical value; restore
 * before paint, including edits that leave that value unchanged. */
export function useFormattedSelection(
  displayed: string,
  alphabet: 'digits' | 'alphanumeric' = 'digits',
) {
  const inputRef = useRef<HTMLInputElement>(null);
  const pending = useRef<{
    start: number;
    end: number;
    nativeStart: number;
    nativeEnd: number;
    raw: string;
  } | null>(null);
  const [edit, setEdit] = useState(0);
  const significant = (text: string) =>
    text.replace(alphabet === 'digits' ? /\D/g : /[^a-z0-9]/gi, '');

  const captureSelection = (field: HTMLInputElement) => {
    pending.current = {
      raw: field.value,
      nativeStart: field.selectionStart ?? field.value.length,
      nativeEnd: field.selectionEnd ?? field.value.length,
      start: significant(
        field.value.slice(0, field.selectionStart ?? field.value.length),
      ).length,
      end: significant(
        field.value.slice(0, field.selectionEnd ?? field.value.length),
      ).length,
    };
    setEdit((n) => n + 1);
  };

  useLayoutEffect(() => {
    const field = inputRef.current;
    const selection = pending.current;
    pending.current = null;
    if (!field || !selection || document.activeElement !== field) return;
    // If only case changed, native offsets include intentionally typed
    // spaces/punctuation. Dropping those would move the caret backwards.
    if (displayed.toUpperCase() === selection.raw.toUpperCase()) {
      field.setSelectionRange(selection.nativeStart, selection.nativeEnd);
      return;
    }
    const position = (count: number) => {
      if (count === 0) return 0;
      let seen = 0;
      for (let i = 0; i < displayed.length; i++) {
        if ((alphabet === 'digits' ? /\d/ : /[a-z0-9]/i).test(displayed[i]))
          seen++;
        if (seen === count) return i + 1;
      }
      return displayed.length;
    };
    field.setSelectionRange(position(selection.start), position(selection.end));
  }, [displayed, edit, alphabet]);

  return { inputRef, captureSelection };
}
